import { create } from 'zustand';
import type { BeadTechnique } from '@bransoletki/shared';
import { floodFill } from '@/lib/flood-fill';
import * as history from '@/lib/undo-history';

export type DrawingTool = 'pencil' | 'eraser' | 'fill' | 'eyedropper' | 'select';

/** Extended palette color with hex for rendering */
export interface EditorPaletteColor {
  readonly index: number;
  readonly beadId: string;
  readonly beadCode: string;
  readonly beadHex: string;
  readonly label: string;
  readonly count: number;
}

/** Rectangular selection region */
export interface Selection {
  readonly startRow: number;
  readonly startCol: number;
  readonly endRow: number;
  readonly endCol: number;
}

/** Clipboard buffer: 2D array of palette indices */
export interface ClipboardBuffer {
  readonly data: readonly (readonly number[])[];
  readonly width: number;
  readonly height: number;
}

interface EditorState {
  // Project
  readonly projectId: string | null;
  readonly projectName: string;
  readonly technique: BeadTechnique;
  readonly gridWidth: number;
  readonly gridHeight: number;
  readonly beadSize: string;

  // Grid data
  readonly grid: readonly (readonly number[])[];
  readonly palette: readonly EditorPaletteColor[];

  // Tools
  readonly activeTool: DrawingTool;
  readonly activeColorIndex: number;

  // Selection & clipboard
  readonly selection: Selection | null;
  readonly clipboard: ClipboardBuffer | null;
  readonly isPasting: boolean;
  readonly pasteRow: number;
  readonly pasteCol: number;

  // View
  readonly zoom: number;
  readonly panX: number;
  readonly panY: number;
  readonly showGrid: boolean;
  readonly previewMode: boolean;

  // Actions — project
  readonly setProjectName: (name: string) => void;
  readonly setTechnique: (technique: BeadTechnique) => void;
  readonly setBeadSize: (size: string) => void;
  readonly initGrid: (width: number, height: number) => void;
  readonly resizeGrid: (width: number, height: number) => void;

  // Actions — drawing
  readonly setCell: (row: number, col: number, colorIndex: number) => void;
  readonly floodFillAt: (row: number, col: number, colorIndex: number) => void;
  readonly eyedrop: (row: number, col: number) => void;
  readonly setPalette: (palette: readonly EditorPaletteColor[]) => void;
  readonly addPaletteColor: (color: EditorPaletteColor) => void;
  readonly setGrid: (grid: readonly (readonly number[])[]) => void;

  // Actions — selection & clipboard
  readonly setSelection: (sel: Selection | null) => void;
  readonly copySelection: () => void;
  readonly startPaste: () => void;
  readonly movePaste: (row: number, col: number) => void;
  readonly confirmPaste: () => void;
  readonly cancelPaste: () => void;

  // Actions — tools
  readonly setActiveTool: (tool: DrawingTool) => void;
  readonly setActiveColorIndex: (index: number) => void;

  // Actions — undo/redo
  readonly pushUndo: () => void;
  readonly undo: () => void;
  readonly redo: () => void;

  // Actions — view
  readonly setZoom: (zoom: number) => void;
  readonly setPan: (x: number, y: number) => void;
  readonly toggleGrid: () => void;
  readonly togglePreview: () => void;
  readonly resetView: () => void;
}

function createEmptyGrid(width: number, height: number): number[][] {
  return Array.from({ length: height }, () => Array.from({ length: width }, () => 0));
}

/** Normalize selection so start <= end */
function normalizeSelection(sel: Selection): Selection {
  return {
    startRow: Math.min(sel.startRow, sel.endRow),
    startCol: Math.min(sel.startCol, sel.endCol),
    endRow: Math.max(sel.startRow, sel.endRow),
    endCol: Math.max(sel.startCol, sel.endCol),
  };
}

export const useEditorStore = create<EditorState>((set, get) => ({
  // Project defaults
  projectId: null,
  projectName: 'Nowy projekt',
  technique: 'loom',
  gridWidth: 20,
  gridHeight: 30,
  beadSize: '11/0',

  // Grid data
  grid: createEmptyGrid(20, 30),
  palette: [],

  // Tools
  activeTool: 'pencil',
  activeColorIndex: 0,

  // Selection & clipboard
  selection: null,
  clipboard: null,
  isPasting: false,
  pasteRow: 0,
  pasteCol: 0,

  // View
  zoom: 1,
  panX: 0,
  panY: 0,
  showGrid: true,
  previewMode: false,

  // ─── Actions — project ─────────────────────────────

  setProjectName: (name) => set({ projectName: name }),
  setTechnique: (technique) => set({ technique }),
  setBeadSize: (beadSize) => set({ beadSize }),
  initGrid: (width, height) =>
    set({
      gridWidth: width,
      gridHeight: height,
      grid: createEmptyGrid(width, height),
      palette: [],
      selection: null,
      clipboard: null,
      isPasting: false,
    }),

  resizeGrid: (newWidth, newHeight) =>
    set((state) => {
      const oldGrid = state.grid;
      const oldW = state.gridWidth;
      const oldH = state.gridHeight;
      if (oldW === newWidth && oldH === newHeight) return state;

      // Tile/repeat existing pattern into new dimensions
      const newGrid = Array.from({ length: newHeight }, (_, r) =>
        Array.from({ length: newWidth }, (_, c) => {
          const srcRow = oldGrid[r % oldH];
          return srcRow ? (srcRow[c % oldW] ?? 0) : 0;
        }),
      );

      return {
        ...state,
        gridWidth: newWidth,
        gridHeight: newHeight,
        grid: newGrid,
        selection: null,
        clipboard: null,
        isPasting: false,
      };
    }),

  // ─── Actions — drawing ─────────────────────────────

  setCell: (row, col, colorIndex) =>
    set((state) => {
      const currentRow = state.grid[row];
      if (!currentRow || currentRow[col] === colorIndex) return state;

      const newRow = [...currentRow];
      newRow[col] = colorIndex;
      const newGrid = [...state.grid];
      newGrid[row] = newRow;
      return { grid: newGrid };
    }),

  floodFillAt: (row, col, colorIndex) =>
    set((state) => ({
      grid: floodFill(state.grid, row, col, colorIndex, state.technique),
    })),

  eyedrop: (row, col) =>
    set((state) => {
      const rowData = state.grid[row];
      if (!rowData) return state;
      const paletteIndex = rowData[col];
      if (paletteIndex === undefined || paletteIndex === 0) return state;
      return { activeColorIndex: paletteIndex, activeTool: 'pencil' };
    }),

  setPalette: (palette) => set({ palette }),
  addPaletteColor: (color) =>
    set((state) => ({ palette: [...state.palette, color] })),
  setGrid: (grid) => set({ grid }),

  // ─── Actions — selection & clipboard ───────────────

  setSelection: (sel) => set({ selection: sel }),

  copySelection: () => {
    const { selection, grid } = get();
    if (!selection) return;
    const norm = normalizeSelection(selection);
    const h = norm.endRow - norm.startRow + 1;
    const w = norm.endCol - norm.startCol + 1;
    const data: number[][] = [];

    for (let r = 0; r < h; r++) {
      const row: number[] = [];
      const srcRow = grid[norm.startRow + r];
      for (let c = 0; c < w; c++) {
        row.push(srcRow?.[norm.startCol + c] ?? 0);
      }
      data.push(row);
    }

    set({ clipboard: { data, width: w, height: h } });
  },

  startPaste: () => {
    const { clipboard } = get();
    if (!clipboard) return;
    set({ isPasting: true, pasteRow: 0, pasteCol: 0, activeTool: 'select' });
  },

  movePaste: (row, col) => set({ pasteRow: row, pasteCol: col }),

  confirmPaste: () => {
    const { clipboard, pasteRow, pasteCol, grid, gridWidth, gridHeight } = get();
    if (!clipboard) return;

    const newGrid = grid.map((row) => [...row]);

    for (let r = 0; r < clipboard.height; r++) {
      const targetRow = pasteRow + r;
      if (targetRow < 0 || targetRow >= gridHeight) continue;
      const srcRow = clipboard.data[r];
      if (!srcRow) continue;

      for (let c = 0; c < clipboard.width; c++) {
        const targetCol = pasteCol + c;
        if (targetCol < 0 || targetCol >= gridWidth) continue;
        const val = srcRow[c];
        if (val !== undefined && val !== 0) {
          const row = newGrid[targetRow];
          if (row) row[targetCol] = val;
        }
      }
    }

    set({ grid: newGrid, isPasting: false, selection: null });
  },

  cancelPaste: () => set({ isPasting: false }),

  // ─── Actions — undo/redo ───────────────────────────

  pushUndo: () => {
    const { grid, palette } = get();
    history.pushSnapshot({ grid, palette });
  },

  undo: () => {
    const { grid, palette } = get();
    const prev = history.undo({ grid, palette });
    if (prev) set({ grid: prev.grid, palette: prev.palette });
  },

  redo: () => {
    const { grid, palette } = get();
    const next = history.redo({ grid, palette });
    if (next) set({ grid: next.grid, palette: next.palette });
  },

  // ─── Actions — tools ───────────────────────────────

  setActiveTool: (tool) => set({ activeTool: tool, isPasting: false }),
  setActiveColorIndex: (index) => set({ activeColorIndex: index }),

  // ─── Actions — view ────────────────────────────────

  setZoom: (zoom) => set({ zoom: Math.max(0.1, Math.min(10, zoom)) }),
  setPan: (x, y) => set({ panX: x, panY: y }),
  toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),
  togglePreview: () =>
    set((state) => ({ previewMode: !state.previewMode, showGrid: state.previewMode })),
  resetView: () => set({ zoom: 1, panX: 0, panY: 0 }),
}));
